import Vue from 'vue'
import Router from 'vue-router'
import Home from './views/Home.vue'
import Login from './views/Login.vue'
import LoginResponse from './views/LoginResponse.vue'
import CookieManager from './helpers/cookiemanager'
import Config from './config'
import { AppState, AppKind } from './state'
import { ActiveUser } from '@/models/user'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: { title: 'Dashboard' }
    },
    {
      path: '/backend/',
      name: 'backend',
      component: () => import('./views/Backend.vue'),
      meta: { title: 'Backend', userNotRequire: true }
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: { title: 'Login', userNotRequire: true }
    },
    {
      path: '/login-response',
      name: 'loginResponse',
      component: LoginResponse,
      meta: { title: 'Login Validation', userNotRequire: true }
    },
    {
      path: '/schedule/:month?',
      name: 'schedule',
      meta: { title: 'Schedule' },
      component: () => import('./views/Schedule.vue')
    },
    {
      path: '/match/:key/',
      name: 'match',
      component: () => import('./views/Match.vue'),
      meta: { title: 'Match' }
    },
    {
      path: '/tournament/:key/',
      name: 'tournament',
      component: () => import('./views/Tournament.vue'),
      meta: { title: 'Tournament' }
    },
    {
      path: '/tournament/:key/round/:roundkey/',
      name: 'tournamentRound',
      component: () => import('./views/TournamentRound.vue'),
      meta: { title: 'Tournament Round' }
    },
    {
      path: '/tournament/:key/team/:teamkey/',
      name: 'tournamentTeam',
      component: () => import('./views/TournamentTeam.vue'),
      meta: { title: 'Tournament Team' }
    },
    {
      path: '/tournament/:key/player/:playerkey/',
      name: 'tournamentPlayer',
      component: () => import('./views/TournamentPlayer.vue'),
      meta: { title: 'Tournament Player' }
    },
    {
      path: '/kabaddi/season/:key/',
      name: 'kabaddiSeason',
      component: () => import('./views/kabaddi/Season.vue'),
      meta: { title: 'Season' }
    },
    {
      path: '/kabaddi/match/:key/',
      name: 'kabaddiMatch',
      component: () => import('./views/kabaddi/Match.vue'),
      meta: { title: 'Kabaddi Match' }
    },
    {
      path: '/kabaddi/season/:key/rounds/',
      name: 'kabaddiSeasonRounds',
      component: () => import('./views/kabaddi/SeasonRounds.vue'),
      meta: { title: 'Season Rounds' }
    },
    {
      path: '/kabaddi/season/:key/points-table/',
      name: 'kabaddiSeasonPointsTable',
      component: () => import('./views/kabaddi/SeasonPointsTable.vue'),
      meta: { title: 'Season Points Table' }
    },
    {
      path: '/kabaddi/season/:key/stats/',
      name: 'kabaddiSeasonStats',
      component: () => import('./views/kabaddi/SeasonStats.vue'),
      meta: { title: 'Season Stats' }
    },
    {
      path: '/kabaddi/season/:key/schedule/',
      name: 'kabaddiSeasonSchedule',
      component: () => import('./views/kabaddi/SeasonSchedule.vue'),
      meta: { title: 'Season Schedule' }
    },
    {
      path: '/kabaddi/season/:key/team/:teamkey/',
      name: 'kabaddiSeasonTeam',
      component: () => import('./views/kabaddi/SeasonTeam.vue'),
      meta: { title: 'Season Team' }
    },
    {
      path: '/kabaddi/season/:key/player/:playerkey/',
      name: 'kabaddiSeasonPlayer',
      component: () => import('./views/kabaddi/SeasonPlayer.vue'),
      meta: { title: 'Season Player' }
    },

    {
      path: '/football/',
      name: 'footballHome',
      component: () => import('./views/football/Home.vue'),
      meta: { title: 'Football Home' }
    },

    {
      path: '/cricket/',
      name: 'cricketHome',
      component: () => import('./views/cricket/Home.vue'),
      meta: { title: 'Cricket Home' }
    },
    {
      path: '/cricket/chatbot/',
      name: 'cricketChatbot',
      component: () => import('./views/cricket/Chatbot.vue'),
      meta: { title: 'Cricket Chatbot' }
    },
    {
      path: '/cricket/match/:key/',
      name: 'cricketMatch',
      component: () => import('./views/cricket/Match.vue'),
      meta: { title: 'Cricket Match' }
    },
    {
      path: '/cricket/player/:key/',
      name: 'cricketPlayer',
      component: () => import('./views/cricket/Player.vue'),
      meta: { title: 'Cricket Player' },
      children: [
        {
          path: 'stats/',
          name: 'cricketPlayerStats',
          component: () => import('./views/cricket/PlayerStats.vue'),
          meta: { title: 'Cricket Player Stats' }
        },
        {
          path: 'performance/:board/:competition/:year?',
          name: 'cricketPlayerPerformance',
          component: () => import('./views/cricket/PlayerPerformance.vue'),
          meta: { title: 'Cricket Player Performance' }
        },
        {
          path: 'timeline/:board/:competition/:year?',
          name: 'cricketPlayerTimeline',
          component: () => import('./views/cricket/PlayerTimeline.vue'),
          meta: { title: 'Cricket Player Timeline' }
        }
      ]
    },
    {
      path: '/cricket/team/:key/',
      name: 'cricketTeam',
      component: () => import('./views/cricket/Team.vue'),
      meta: { title: 'Cricket Team' },
      children: [
        {
          path: 'performance/:board/:competition/:year?',
          name: 'cricketTeamPerformance',
          component: () => import('./views/cricket/TeamPerformance.vue'),
          meta: { title: 'Cricket Team Performance' }
        },
        {
          path: 'timeline/:board/:competition/:year?',
          name: 'cricketTeamTimeline',
          component: () => import('./views/cricket/TeamTimeline.vue'),
          meta: { title: 'Cricket Team Timeline' }
        }
      ]
    },
    {
      path: '/cricket/season/:key/',
      name: 'cricketSeason',
      component: () => import('./views/cricket/Season.vue'),
      meta: { title: 'Cricket Season' }
    },
    {
      path: '/cricket/season/:key/schedule/:month?',
      name: 'cricketSeasonSchedule',
      component: () => import('./views/Schedule.vue'),
      meta: { title: 'Season Schedule' },
      props: { forSport: 'cricket', forTournament: true }
    },
    {
      path: '/cricket/season/:key/points/',
      name: 'cricketSeasonPoints',
      component: () => import('./views/cricket/SeasonPoints.vue'),
      meta: { title: 'Season Points' }
    },
    {
      path: '/cricket/season/:key/stats/',
      name: 'cricketSeasonStats',
      component: () => import('./views/cricket/SeasonStats.vue'),
      meta: { title: 'Season Stats' }
    },
    {
      path: '/cricket/season/:key/team/:teamkey/',
      name: 'cricketSeasonTeam',
      component: () => import('./views/cricket/SeasonTeam.vue'),
      meta: { title: 'Season Team' }
    },
    {
      path: '/cricket/fantasy-contest/:key/',
      name: 'cricketFantasyContest',
      component: () => import('./views/cricket/FantasyContest.vue'),
      meta: { title: 'Cricket Fantasy Contest' }
    },
    {
      path: '/workflow/',
      name: 'WorkflowHome',
      component: () => import('./views/workflow/Home.vue'),
      meta: { title: 'Workflow Home' }
    },
    {
      path: '/no-appkey/',
      name: 'Error',
      component: () => import('./views/workflow/Error.vue'),
      meta: { title: 'Error' }
    },
    {
      path: '/workflow/cricket/fantasy-credit-edit/match/:key/',
      name: 'EditPoints',
      component: () => import('./views/workflow/cricket/EditPlayerPoints.vue'),
      meta: { title: 'Edit Player Points' },
      children: [
        {
          path: 'view-history/',
          name: 'viewHistory',
          component: () => import('./views/workflow/cricket/ViewHistory.vue'),
          meta: { title: 'View History' }
        }
      ]
    },
    {
      path: '/workflow/cricket/schedule/:month?',
      name: 'WorkflowCricketSchedule',
      meta: { title: 'Cricket Schedule', sport: 'cricket' },
      component: () => import('./views/workflow/cricket/CricketSchedule.vue')
    },
    {
      path: '/workflow/recent-edits/',
      name: 'RecentEditPage',
      component: () => import('./views/workflow/cricket/RecentEdit.vue'),
      meta: { title: 'Recent Edit points' }
    },
    {
      path: '/workflow/football/fantasy-credit-edit/match/:key/',
      name: 'FootballEditPoints',
      component: () => import('./views/workflow/football/FootballEditPlayerPoints.vue'),
      meta: { title: 'Football Edit Player Points' },
      children: [
        {
          path: 'football-view-history/',
          name: 'FootballViewHistory',
          component: () => import('./views/workflow/football/FootballViewHistory.vue'),
          meta: { title: 'Football View History' }
        }
      ]
    },
    {
      path: '/cricket/v4/metric-101/',
      name: 'MetricHome',
      component: () => import('./views/Metric101/MetricHome.vue'),
      meta: { title: 'Metric 101' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/season/:key',
      name: 'MetricSeason',
      component: () => import('./views/cricket/Season.vue'),
      meta: { title: 'Metric Cricket Season' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/season/:key/schedule/:month?',
      name: 'MetricSeasonSchedule',
      component: () => import('./views/Schedule.vue'),
      meta: { title: 'Cricket Season Schedule' },
      props: { forSport: 'cricket', forTournament: true, isMetric: true }
    },
    {
      path: '/cricket/v4/season/:key/points/',
      name: 'MetricSeasonPoints',
      component: () => import('./views/cricket/SeasonPoints.vue'),
      meta: { title: 'Season Points' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/season/:key/stats/',
      name: 'MetricSeasonStats',
      component: () => import('./views/cricket/SeasonStats.vue'),
      meta: { title: 'Season Stats' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/season/:key/team/:teamkey/',
      name: 'MetricSeasonTeam',
      component: () => import('./views/cricket/SeasonTeam.vue'),
      meta: { title: 'Season Team' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/match/:key/',
      name: 'MetricCricketMatch',
      component: () => import('./views/cricket/Match.vue'),
      meta: { title: 'Cricket Match' },
      props: { isMetric: true }
    },
    {
      path: '/cricket/v4/team/:key/',
      name: 'MetricCricketTeam',
      component: () => import('./views/cricket/Team.vue'),
      meta: { title: 'Cricket Team' },
      props: { isMetric: true }
    },

    {
      path: '/fantasy-game',
      name: 'FG',
      component: () => import('./views/fantasygame/Base.vue'),
      meta: { title: 'Fantasy Game' },

      children: [
        {
          name: 'FGHome',
          path: '',
          component: () => import('./views/fantasygame/Home.vue'),
          meta: { title: 'Fantasy Game' }
        },
        {
          path: 'contest/:key',
          name: 'FGContest',
          component: () => import('./views/fantasygame/Contest.vue'),
          meta: { title: 'Fantasy Contest' }
        },
        {
          path: 'contest/:contestKey/game/:gameKey',
          name: 'FGGame',
          component: () => import('./views/fantasygame/Game.vue'),
          meta: { title: 'Fantasy Game' }
        },
        {
          path: 'contest/:contestKey/game/:gameKey/play',
          name: 'FGGamePlay',
          component: () => import('./views/fantasygame/GamePlay.vue'),
          meta: { title: 'Fantasy Game Play' }
        },
        {
          path: 'contest/:contestKey/game/:gameKey/teams',
          name: 'FGGameTeams',
          component: () => import('./views/fantasygame/GameTeams.vue'),
          meta: { title: 'Fantasy Game Teams' }
        },
        {
          path: 'contest/:contestKey/game/:gameKey/team/:teamKey',
          name: 'FGGameTeamEdit',
          component: () => import('./views/fantasygame/GameTeamEdit.vue'),
          meta: { title: 'Fantasy Game Team Edit' }
        },
        {
          path: 'contest/:contestKey/game/:gameKey/team/',
          name: 'FGGameTeamCreate',
          component: () => import('./views/fantasygame/GameTeamEdit.vue'),
          meta: { title: 'Fantasy Game Team Edit' }
        }
      ]
    },
    {
      path: '/fantasy-console',
      name: 'FC',
      component: () => import('./views/fantasyconsole/Base.vue'),
      meta: { title: 'Fantasy Console' },
      children: [
        {
          name: 'FCHome',
          path: '',
          component: () => import('./views/fantasyconsole/Home.vue'),
          meta: { title: 'Fantasy Console' }
        },
        {
          path: 'contest/',
          name: 'FCCreateContest',
          component: () => import('./views/fantasyconsole/CreateContest.vue'),
          meta: { title: 'Create Contest' }
        },
        {
          path: 'all-contests/',
          name: 'AllContests',
          component: () => import('./views/fantasyconsole/AllContests.vue'),
          meta: { title: 'All Contests' }
        },
        {
          path: 'contest-confirmation/:key',
          name: 'ContestConfirmation',
          component: () => import('./views/fantasyconsole/ContestConfirmation.vue'),
          meta: { title: 'Contest Created Successfully' }
        },
        {
          path: 'contest/:key',
          name: 'FCEditContest',
          component: () => import('./views/fantasyconsole/CreateContest.vue'),
          meta: { title: 'Edit Contest' }
        },
        {
          path: 'contest/:key/game/',
          name: 'FCCreateGame',
          component: () => import('./views/fantasyconsole/CreateGame.vue'),
          meta: { title: 'Create Game' }
        },
        {
          path: 'contest/:key/game/:gameKey',
          name: 'FCEditGame',
          component: () => import('./views/fantasyconsole/CreateGame.vue'),
          meta: { title: 'Create Game' }
        },
        {
          path: 'contest/:key/game/:gameKey/preview',
          name: 'FCPreviewGame',
          component: () => import('./views/fantasyconsole/Game.vue'),
          meta: { title: 'Preview Game' }
        },
        {
          path: 'rule/',
          name: 'CreateRule',
          component: () => import('./views/fantasyconsole/CreateRule.vue'),
          meta: { title: 'Create Rule' }
        },
        {
          path: 'rule/:key/',
          name: 'EditRule',
          component: () => import('./views/fantasyconsole/CreateRule.vue'),
          meta: { title: 'Edit Rule' }
        },
        {
          path: 'reward/',
          name: 'CreateReward',
          component: () => import('./views/fantasyconsole/CreateReward.vue'),
          meta: { title: 'Create Reward' }
        },
        {
          path: 'reward/:key/',
          name: 'EditReward',
          component: () => import('./views/fantasyconsole/CreateReward.vue'),
          meta: { title: 'Edit Reward' }
        }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  AppState.resetBreadcrumb()

  let docTitle = Config.title

  // Ref: https://alligator.io/vuejs/vue-router-modify-head/
  const docTitleFound = to.matched.slice().reverse().find(r => r.meta && r.meta.title)
  const pageTitleFound = to.matched.slice().reverse().find(r => r.meta && r.meta.pageTitle)

  if (docTitleFound) {
    docTitle = docTitleFound.meta.title
  }

  let pageTitle = docTitle
  if (pageTitleFound) {
    /* eslint-disable prefer-destructuring */
    pageTitle = pageTitleFound.meta.pageTitle
  }

  AppState.updateTitles(docTitle, pageTitle)
  AppState.updateActivePage(to.name)
  AppState.updateAppKind(AppKind.Litzscore)

  if (to.matched.some(record => record.meta.userNotRequire)) {
    next()
  } else {
    const token = CookieManager.getItem('rzAccessToken')
    let forAppkey = CookieManager.getItem('rzForAppkey')
    if (!forAppkey) {
      if (to.query && to.query.forAppkey) {
        forAppkey = to.query.forAppkey
      }
    }
    if (forAppkey !== null) {
      CookieManager.setItem('rzForAppkey', forAppkey, Number.Infinity, '/')
    }
    if (token === undefined || token === null || token === '') {
      next({
        path: '/login',
        query: {
          redirect: to.fullPath
        }
      })
    } else if (!ActiveUser.user.key) {
      AppState.updateUserLoggedIn(true)
      ActiveUser.user.updateFromServer(ActiveUser.state).then(() => {
        console.log('Fetching User Profile')
        if ((to.path.indexOf('/workflow')) > -1) {
          AppState.updateAppKind(AppKind.Workflow)
          if (forAppkey === undefined || forAppkey === null || forAppkey === '') {
            next({
              path: '/no-appkey',
              query: {
                redirect: to.fullPath
              }
            })
          }
        }
        next()
      })
    } else if ((to.path.indexOf('/workflow')) > -1 && (forAppkey === undefined || forAppkey === null || forAppkey === '')) {
      console.log('workflow no app-key')
      AppState.updateAppKind(AppKind.Workflow)
      next({
        path: '/no-appkey',
        query: {
          redirect: to.fullPath
        }
      })
    } else {
      console.log('Skipping', to.fullPath)
      if ((to.path.indexOf('/workflow')) > -1) {
        AppState.updateAppKind(AppKind.Workflow)
        if (forAppkey === undefined || forAppkey === null || forAppkey === '') {
          next({
            path: '/no-appkey',
            query: {
              redirect: to.fullPath
            }
          })
        }
      }
      next()
    }
  }
})

router.beforeResolve((to, from, next) => {
  if ((to.path.indexOf('/fantasy-game')) > -1) {
    AppState.updateAppTheme('fantasygame')
  } else if ((to.path.indexOf('/fantasy-console')) > -1) {
    AppState.updateAppTheme('fantasyconsole')
  } else {
    AppState.resetAppTheme()
  }

  next()
})

export default router
